import { difference, intersection, union } from "lodash-es";
import { PureComponent } from "react";
import { connect } from "react-redux";

import { CalloutIntent, LoadingSpinner } from "widgets";

import { Checkbox } from "./forms/Checkbox.js";
import { Dialog, type IDialogMessage } from "./overlays/Dialogs.js";
import { type InputTableQuerySpec, type InputUserGroupInput, type UserGroup } from "@/graphql.ts";
import { getProjectLabel } from "@/redux/modules/config.ts";
import { type IIdentifiable, type ITableData, reloadTableRows } from "@/redux/modules/table-data.ts";
import { type IUserGroupAdminState, editMultipleUserGroupAssignments } from "@/redux/modules/user-group-admin.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { ENTITY_TYPE, PUBLIC_PROJECT_GROUP_ID } from "@/settings/settings.ts";

interface IUserGroupsAssignmentButtonOwnProps {
  activeTable: ITableData;

  onApply: () => void;
  onCancel: () => void;
}

interface IUserGroupsAssignmentButtonAppStateProps {
  userGroupAdminState: IUserGroupAdminState;
}

interface IUserGroupsAssignmentButtonDispatchProps {
  editMultipleUserGroupAssignments: (assignments: InputUserGroupInput[]) => Promise<any>;
  reloadTableRows: () => void;
}

export interface IUserGroupsAssignmentButtonProps
  extends IUserGroupsAssignmentButtonAppStateProps,
    IUserGroupsAssignmentButtonDispatchProps,
    IUserGroupsAssignmentButtonOwnProps {}

function mapStateToProps(
  state: IAppState,
  _ownProps: IUserGroupsAssignmentButtonOwnProps,
): IUserGroupsAssignmentButtonAppStateProps {
  return {
    userGroupAdminState: state.userGroupAdmin,
  };
}

function mapDispatchToProps(
  dispatch,
  ownProps: IUserGroupsAssignmentButtonOwnProps,
): IUserGroupsAssignmentButtonDispatchProps {
  return {
    editMultipleUserGroupAssignments: (assignments: InputUserGroupInput[]) =>
      dispatch(editMultipleUserGroupAssignments(assignments)),
    reloadTableRows: () => dispatch(reloadTableRows(ownProps.activeTable.tableQuerySpec, true)), // reloading the rows is not enough, the metadata should also be fetched again to have updated counts
  };
}

interface IUserGroupsAssignmentButtonState {
  userGroupAssignments?: IUserGroupAssignment[];
  submittingAssignment?: boolean;
  assignmentError?: Error;
}

interface IUserGroupAssignment {
  userGroup: UserGroup;
  assignments: number[]; // ids of either sample types, organisms or users
  initialAssignmentModel: ASSIGNMENT_MODEL; // drive the warning
  assignmentModel: ASSIGNMENT_MODEL; // drive the sorting and the checkbox visual
  userModified: boolean; // to find out if the list of assignments has been modified by the user and needs to be commited
}

enum ASSIGNMENT_MODEL {
  ALL_SELECTED = 2,
  PARTIALLY_SELECTED = 1,
  NONE_SELECTED = 0,
}

/**
 * Button and popover dialog that allow to assign organisms or users to one to many User Groups.
 *
 * created on 8/22/2017
 */
class UserGroupsAssignmentButton extends PureComponent<
  IUserGroupsAssignmentButtonProps,
  IUserGroupsAssignmentButtonState
> {
  constructor(props: IUserGroupsAssignmentButtonProps) {
    super(props);

    this.applyAssignments = this.applyAssignments.bind(this);
    this.onAssignmentChanged = this.onAssignmentChanged.bind(this);

    this.state = {
      userGroupAssignments: [],
      submittingAssignment: false,
      assignmentError: null,
    };
  }

  initializeLocalUserGroupAssignments() {
    // init the local state with group assignments as set in global redux store
    const localUserGroupAssignments: IUserGroupAssignment[] = this.props.userGroupAdminState.userGroups
      .map((userGroup: UserGroup) => {
        const assignments: number[] = this.getAssignedEntities(userGroup).map(
          (identifiable: IIdentifiable) => identifiable.id,
        );
        const initialAssignmentModel: ASSIGNMENT_MODEL = this.returnAssignmentModel(assignments);
        const userGroupAssignment: IUserGroupAssignment = {
          userGroup: userGroup,
          assignments: assignments,
          assignmentModel: initialAssignmentModel,
          initialAssignmentModel: initialAssignmentModel,
          userModified: false,
        };
        return userGroupAssignment;
      })
      // we sort on the assignment model + alphabetically
      .sort((checkboxData1: IUserGroupAssignment, checkboxData2: IUserGroupAssignment) => {
        return checkboxData1.assignmentModel > checkboxData2.assignmentModel
          ? -1
          : checkboxData1.assignmentModel < checkboxData2.assignmentModel
            ? 1
            : checkboxData1.userGroup.name.toLowerCase().localeCompare(checkboxData2.userGroup.name.toLowerCase());
      });
    this.setState({ userGroupAssignments: localUserGroupAssignments });
  }

  componentDidMount() {
    this.initializeLocalUserGroupAssignments();
  }

  componentDidUpdate(prevProps: Readonly<IUserGroupsAssignmentButtonProps>) {
    if (this.props.userGroupAdminState.isLoadingUserGroups !== prevProps.userGroupAdminState.isLoadingUserGroups) {
      this.initializeLocalUserGroupAssignments();
    }
  }

  getAssignedEntities(userGroup: UserGroup): IIdentifiable[] {
    if (this.props.activeTable?.tableQuerySpec) {
      const tableSpec: InputTableQuerySpec = this.props.activeTable.tableQuerySpec;
      switch (tableSpec.resultType) {
        case ENTITY_TYPE.ORGANISM:
          return userGroup.organisms;
        case ENTITY_TYPE.USER:
          return userGroup.members;
        case ENTITY_TYPE.SAMPLE_TYPE:
          return userGroup.sampleTypes;
        default:
          throw Error("unsupported user group assignment");
      }
    }
    return [];
  }

  returnAssignmentModel(assignedIds: number[] = []): ASSIGNMENT_MODEL {
    const selectedIds: number[] = this.props.activeTable.selectionModel.selectedIds as number[];

    const intersectionArray: number[] = intersection(selectedIds, assignedIds);
    if (intersectionArray.length === selectedIds.length) {
      // all selected are already assigned to this user group
      return ASSIGNMENT_MODEL.ALL_SELECTED;
    } else if (intersectionArray.length > 0) {
      // some selected are already assigned
      return ASSIGNMENT_MODEL.PARTIALLY_SELECTED;
    } else {
      // none is selected
      return ASSIGNMENT_MODEL.NONE_SELECTED;
    }
  }

  onAssignmentChanged(userGroup: UserGroup, event) {
    const selectionAssigned: boolean = event.target.checked;
    const previousUserGroupAssignment: IUserGroupAssignment = this.state.userGroupAssignments.find(
      (uga: IUserGroupAssignment) => uga.userGroup.id === userGroup.id,
    );
    const assignedIds: number[] = previousUserGroupAssignment.assignments;
    const selectedIds: number[] = this.props.activeTable.selectionModel.selectedIds as number[];

    const newSelection: number[] = selectionAssigned
      ? union(assignedIds, selectedIds)
      : difference(assignedIds, selectedIds);
    const userGroupAssignment: IUserGroupAssignment = {
      userGroup: userGroup,
      assignments: newSelection,
      initialAssignmentModel: previousUserGroupAssignment.initialAssignmentModel,
      assignmentModel: this.returnAssignmentModel(newSelection),
      userModified: true,
    };

    const newUserGroupAssignments: IUserGroupAssignment[] = this.state.userGroupAssignments.map(
      (uga: IUserGroupAssignment) => (uga.userGroup.id === userGroup.id ? userGroupAssignment : uga),
    );
    this.setState({ userGroupAssignments: newUserGroupAssignments });
  }

  createCheckbox(userGroupAssignment: IUserGroupAssignment) {
    const userGroup: UserGroup = userGroupAssignment.userGroup;
    const assignmentModel: ASSIGNMENT_MODEL = userGroupAssignment.assignmentModel;
    const tableSpec: InputTableQuerySpec = this.props.activeTable?.tableQuerySpec; // to check whether we are in the context of sample types, users or organisms assignment
    return (
      <div className="checkbox" key={"usergroup_" + userGroup.id}>
        <Checkbox
          id={"usergroup_" + userGroup.id}
          label={userGroup.name}
          tooltipText={userGroup.description}
          disabled={userGroup.id === PUBLIC_PROJECT_GROUP_ID && tableSpec.resultType === ENTITY_TYPE.USER}
          checked={assignmentModel === ASSIGNMENT_MODEL.ALL_SELECTED}
          partiallyChecked={assignmentModel === ASSIGNMENT_MODEL.PARTIALLY_SELECTED}
          onChange={(event) => this.onAssignmentChanged(userGroup, event)}
        />
      </div>
    );
  }

  applyAssignments() {
    this.setState({ submittingAssignment: true });
    const tableSpec: InputTableQuerySpec = this.props.activeTable.tableQuerySpec; // to check whether we are in the context of sample types, users or organisms assignment

    const assignments: InputUserGroupInput[] = this.state.userGroupAssignments
      .filter((userGroupAssignment: IUserGroupAssignment) => userGroupAssignment.userModified)
      .map((userGroupAssignment: IUserGroupAssignment) => {
        return {
          userGroupId: userGroupAssignment.userGroup.id,
          name: null,
          description: null,
          organismIds: tableSpec.resultType === ENTITY_TYPE.ORGANISM ? userGroupAssignment.assignments : null,
          sampleTypeIds: tableSpec.resultType === ENTITY_TYPE.SAMPLE_TYPE ? userGroupAssignment.assignments : null,
          sessionIds: tableSpec.resultType === ENTITY_TYPE.USER ? userGroupAssignment.assignments : null,
        };
      });

    this.props
      .editMultipleUserGroupAssignments(assignments)
      .then(() => {
        this.props.reloadTableRows();
        this.props.onApply();
      })
      .catch((error: Error) => {
        this.setState({ submittingAssignment: false, assignmentError: error });
      });
  }

  render() {
    // we show a warning when we remove user(s) from User Groups
    const showWarning: boolean =
      this.props.activeTable.tableQuerySpec.resultType === ENTITY_TYPE.USER &&
      !!this.state.userGroupAssignments.find(
        (uga: IUserGroupAssignment) =>
          uga.userModified &&
          uga.initialAssignmentModel !== ASSIGNMENT_MODEL.NONE_SELECTED &&
          uga.assignmentModel === ASSIGNMENT_MODEL.NONE_SELECTED,
      );

    const errorFooterMessage: IDialogMessage = this.state.assignmentError && {
      message: this.state.assignmentError.message,
    };
    const warningFooterMessage: IDialogMessage = showWarning && {
      message: (
        <span>
          <strong>Warning: </strong>Removing a user from a user group will remove the user from all{" "}
          {getProjectLabel(false, true)} with that user group.
        </span>
      ),
      intent: CalloutIntent.Warning,
    };

    return (
      <Dialog
        closeFn={this.props.onCancel}
        mainActionButtonProps={{
          isLoading: this.state.submittingAssignment,
          isDisabled: this.props.userGroupAdminState.isLoadingUserGroups,
          children: "Apply",
          onPress: this.applyAssignments,
        }}
        footerMessage={errorFooterMessage || warningFooterMessage}
      >
        {this.props.userGroupAdminState.isLoadingUserGroups ? (
          <LoadingSpinner />
        ) : (
          this.state.userGroupAssignments.map((userGroupAssignment: IUserGroupAssignment) =>
            this.createCheckbox(userGroupAssignment),
          )
        )}
      </Dialog>
    );
  }
}

export default connect<
  IUserGroupsAssignmentButtonAppStateProps,
  IUserGroupsAssignmentButtonDispatchProps,
  IUserGroupsAssignmentButtonOwnProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(UserGroupsAssignmentButton);
